'use client';

import { setLangOverride, clearLangOverride } from '@/hooks/useAutoLang';
import { getLangPref } from '@/lib/user-preferences';
import { NavDropdown, NavDropdownItem } from './NavDropdown';
import { uiFb } from '@/lib/i18n';

interface LangAlternate {
  lang: string;
  slug: string;
  label: string;
  labelEn?: string;
}

interface NavLanguagePickerProps {
  langAlternates: LangAlternate[];
  currentLang: string;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
}

export function NavLanguagePicker({ langAlternates, currentLang, isOpen, onToggle, onClose }: NavLanguagePickerProps) {
  if (langAlternates.length <= 1) return null;

  const hasOverride = typeof window !== 'undefined' && !!getLangPref();
  const currentLabel = langAlternates.find(a => a.lang === currentLang)?.label ?? currentLang.toUpperCase();

  function handleAutoDetect() {
    clearLangOverride();
    onClose();
    // Reload to let useAutoLang run browser detection without an override
    window.location.reload();
  }

  return (
    <NavDropdown
      isOpen={isOpen}
      onToggle={onToggle}
      onClose={onClose}
      label={uiFb('ui.select_language', 'Select language', currentLang)}
      trigger={
        <span className="flex items-center gap-1.5">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M2 12h20M2 12a10 10 0 1020 0M2 12a10 10 0 0020 0M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10A15.3 15.3 0 0112 2" />
          </svg>
          <span>{currentLabel}</span>
        </span>
      }
    >
      <NavDropdownItem
        label={uiFb('ui.auto_detect', 'Auto-detect', currentLang)}
        isActive={!hasOverride}
        onClick={handleAutoDetect}
      />
      {langAlternates.map(alt => (
        <NavDropdownItem
          key={alt.lang}
          label={alt.label}
          subLabel={alt.labelEn && alt.labelEn !== alt.label ? alt.labelEn : undefined}
          isActive={hasOverride && alt.lang === currentLang}
          href={`/${alt.slug}`}
          onClick={() => { setLangOverride(alt.lang); onClose(); }}
        />
      ))}
    </NavDropdown>
  );
}
